import Settings from '@/settings'
import Auth from "./auth";
import BootstrapService from './bootstrapService'
import axios from "axios";
import RadioCacheService from '../services/radioCacheService'
import PrefsService from "./PrefsService"

/**
 * Checks the URL's query portion for a c=<something> parameter. If we find one, return that <something> as a customer.
 * @returns {string}
 */
function customerFromQuery()
{
    // URLSearchParams doesn't work in IE, so just bail out in that case
    if ('URLSearchParams' in window)
    {
        let s = window.location.search.substring(1);
        let sp = new URLSearchParams(s)
        return sp.get('c')
    }
}

export default {
    currentUser: null,
    /**
     * Initializes the user session and populates User information, User's current and available customers, and the application version
     * @returns {Promise<{user: {userId: *, name: (*|null), customer: (*|null), customers: [], permissions: (*|*[]|[]), roles: (*|*[])}, version: (string|null)}>}
     */
    async initialize() {
        let bootstrap = await this.bootstrap()
        Auth.setFeatures(bootstrap.features)
        return bootstrap
    },

    /**
     * @returns {Promise<{user: {userId: *, name: (*|null), customer: (*|null), customers: [], permissions: (*|*[]|[]), roles: (*|*[])}, version: (string|null), features: (*|null)>}
     */
    async bootstrap () {
        let response = await axios.get('/web-support/bootstrap')
        if (!response) {
            // If we get a bad response, reject the promise
            throw new Error("bad bootstrap response object");
        }

        if (response.request.responseURL) {
            response.data.settings.origin = new URL(response.request.responseURL).origin
            axios.defaults.baseURL = response.data.settings.origin
        } else {
            response.data.settings.origin = ""
        }
        BootstrapService.initialize(response.data)
        Settings.storeSettings(response.data.settings)
        return response.data;
    },

    async selectDefaultCustomer(userObj)
    {
        function hasCustomer(c) {
            return userObj.customers && userObj.customers.length > 0 && userObj.customers.find(element => element.name === c)
        }

        function validateCustomer(customer) {
            if (customer && hasCustomer(customer)) {
                return customer;
            }
            return null;
        }

        // First, check if a customer was provided as ?c=<custId>
        let lastCustomer = validateCustomer(customerFromQuery());

        // If we didn't find a customer in the query string, check for one in session storage
        if (!lastCustomer) {
            lastCustomer = validateCustomer(window.sessionStorage.getItem('ncs.customer'));
        }

        // If we didn't find a customer in the query string or session storage, look up the last used customer in user prefs
        if (!lastCustomer) {
            // This should not happen, however, it does, so we will handle it so the user does not get an empty gui
            lastCustomer = validateCustomer(PrefsService.get('lastCustomer'))
        }

        // If set, then get the object from the list of customers and store in the userObj
        if (lastCustomer)
        {
            userObj.customer = userObj.customers.find(element => element.name === lastCustomer)
        }
        // else make sure we have a valid customer list and use the first customer in the list
        else if (userObj.customers && userObj.customers.length > 0 && userObj.customers[0].name)
        {
            // Sort the list first alphabetically
            userObj.customers.sort (function (a, b) {
                let aName = a.displayName || a.name
                let bName = b.displayName || b.name
                return aName.localeCompare (bName)
            })
            userObj.customer = userObj.customers[0]
        }
        else // Else you have no valid customers
        {
            userObj.customer = null;
        }
        return this.provisionUserSession(userObj)
    },

    /**
     * @returns {Promise<{user: {userId: *, name: (*|null), customer: (*|null), customers: [], permissions: (*|*[]|[]), roles: (*|*[])}, version: (string|null)}>}
     */
    async provisionUserSession(userObj)
    {
        if (userObj.customer)
        {
            window.sessionStorage.setItem('ncs.customer', userObj.customer.name)
            Auth.addCustomerHeader(userObj.customer.name);
            PrefsService.put('lastCustomer', userObj.customer.name)
        }
        this.currentUser = userObj

        let name = userObj.customer ? userObj.customer.name : undefined
        try
        {
            await RadioCacheService.init(userObj.userId, name)
        }
        catch(error)
        {
            console.error('Error initializing RadioCacheService:', error)
        }
        return userObj
    },
    hasPermission (permission)
    {
        if (!permission || typeof permission !== 'string')
        {
            return false
        }
        return this.currentUser?.permissions?.findIndex(item => permission.toLowerCase() === item.toLowerCase()) !== -1
    },
    getCurrentUser()
    {
        if (!this.currentUser)
        {
            throw new Error('User session not initialized. Please call initialize() first.')
        }
        return this.currentUser
    },
    hasCustomer()
    {
        if (!this.currentUser)
        {
            throw new Error('User session not initialized. Please call initialize() first.')
        }
        return (this.currentUser.customer != null)
    },
    getCurrentCustomer()
    {
        if (!this.currentUser)
        {
            throw new Error('User session not initialized. Please call initialize() first.')
        }
        return this.currentUser.customer || null
    },
    getCustomerTimezone()
    {
        return (this.currentUser.customer?.timezone ? this.currentUser.customer.timezone : 'UTC')
    },
    getCustomerRetentionDays()
    {
        return this.currentUser.customer?.dataRetentionDays
    },
    getCustomerRetentionDaysEndpoints()
    {
        return this.currentUser.customer?.dataRetentionDays?.ENDPOINTS
    },
    showNATuningContent() {
        return (Auth.isNorthAmerica() && Auth.isTuningFeatureEnabled() && this.hasPermission('MANAGE_RADIOS'))
    },
    showArqivaTuningContent() {
        return (Auth.isArqiva() && this.hasPermission('MANAGE_RADIOS'))
    },
    showGeneralTuningContent() {
        return (Auth.isTuningFeatureEnabled() && this.hasPermission('MANAGE_RADIOS'))
    },
    hasTowerPermission () {
        return this.hasPermission('VIEW_TOWERS') || this.hasPermission('MANAGE_TOWERS')
    },
    hasRadioPermission () {
        return this.hasPermission('VIEW_RADIOS') || this.hasPermission('MANAGE_RADIOS')
    },
    hasAlarmPermission () {
        return this.hasPermission('VIEW_ALARMS') || this.hasPermission('MANAGE_ALARMS')
    },
    hasFreqMapPermission () {  
        return this.hasPermission("MANAGE_FREQUENCY_MAPPINGS") || this.hasPermission("VIEW_FREQUENCY_MAPPINGS")
    },
    hasGlobalAlarmsPermission () {
        return (Auth.isGlobalAlarmFeatureEnabled() && this.hasPermission('MANAGE_GLOBAL_ALARMS'))
    },
    hasAlarmsSuppressionPermission () {
        return (Auth.isAlarmSuppressionFeatureEnabled() && this.hasPermission('MANAGE_ALARM_SUPPRESSIONS'))
    },
    getTuningThresholdsSetting(settingName)
    {
        return this.currentUser.customer?.tuneThresholds[settingName]
    },
}
